import React, { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Grid,
  Slider,
  TextField,
  Divider,
  Box,
  InputAdornment,
} from "@mui/material";
import InfoButtonModal from "../mortage-info-modal";
import CurrencyFormatter from "../currency-formatter";
import { GeoZone } from "@serviestate/shared";
import { useTranslation } from "next-i18next";

interface MortgageCalculatorProps {
  defaultPropertyPrice?: number;
  defaultDownPayment?: number;
  defaultLoanTerm?: number;
  defaultLoanInterest?: number;
  ccaa: GeoZone;
}

const MortgageCalculator: React.FC<MortgageCalculatorProps> = ({
  defaultPropertyPrice = 180000,
  defaultDownPayment = 54000,
  defaultLoanTerm = 30,
  defaultLoanInterest = 2.5,
  ccaa,
}) => {
  const { t } = useTranslation();
  const [propertyPrice, setPropertyPrice] = useState(defaultPropertyPrice);
  const [formattedPropertyPrice, setFormattedPropertyPrice] = useState(
    CurrencyFormatter(defaultPropertyPrice)
  );
  const [downPayment, setDownPayment] = useState(defaultDownPayment);
  const [formattedDownPayment, setFormattedDownPayment] = useState(
    CurrencyFormatter(defaultDownPayment, false)
  );
  const [loanTerm, setLoanTerm] = useState(defaultLoanTerm);
  const [loanInterest, setLoanInterest] = useState(defaultLoanInterest);
  const [notaryExpenses, setNotaryExpenses] = useState<number>(0);
  const [registryExpenses, setRegistryExpenses] = useState<number>(0);
  const [taxesExpenses, setTaxesExpenses] = useState<number>(0);
  const [totalExpenses, setTotalExpenses] = useState<number>(0);
  const [loanValue, setLoanValue] = useState<number>(0);
  const [mortgagePayment, setMortgagePayment] = useState<number>(0);

  useEffect(() => {
    const taxPercentages: Record<string, number> = {
      Andalucía: 7,
      Aragón: 8,
      Asturias: 8,
      Baleares: 8,
      Canarias: 6.5,
      Cantabria: 9,
      "Castilla-La Mancha": 9,
      "Castilla y León": 8,
      Cataluña: 10,
      Ceuta: 6,
      Madrid: 6,
      "Comunitat Valenciana": 10,
      Extremadura: 8,
      Galicia: 10,
      "La Rioja": 7,
      Melilla: 6,
      "Región de Murcia": 8,
      Navarra: 6,
      "País Vasco": 4,
    };

    const taxesCCAA = taxPercentages[ccaa.name] ?? 8;

    const notaryExpensePercentage = 0.0029;
    const registryExpensePercentage = 0.0021;

    const notaryExpensesValue = propertyPrice * notaryExpensePercentage;
    const registryExpensesValue = propertyPrice * registryExpensePercentage;
    // TODO, adaptar según comunidad
    const taxesExpensesValue = propertyPrice * (taxesCCAA / 100);
    const totalExpensesValue =
      notaryExpensesValue + registryExpensesValue + taxesExpensesValue;
    const loanValue =
      propertyPrice + totalExpensesValue - downPayment > 0
        ? propertyPrice + totalExpensesValue - downPayment
        : 0;
    const rate = loanInterest / 100 / 12;
    const months = loanTerm * 12;
    const mortgageValue = parseFloat(
      ((rate * loanValue) / (1 - Math.pow(1 + rate, -months))).toFixed(2)
    );

    // const totalInterestsValue = mortgageValue * months - loanValue;
    // const totalOperationCost =
    //   loanValue + totalInterestsValue + totalExpenses + downPayment;

    setNotaryExpenses(notaryExpensesValue);
    setRegistryExpenses(registryExpensesValue);
    setTaxesExpenses(taxesExpensesValue);
    setTotalExpenses(totalExpensesValue);
    setLoanValue(loanValue);
    setMortgagePayment(mortgageValue);
  }, [propertyPrice, downPayment, loanTerm, loanInterest, totalExpenses, ccaa]);

  const handleSliderChange =
    (name: string) => (_event: Event, newValue: number | number[]) => {
      const numericValue = newValue as number;
      if (name === "propertyPrice") {
        setPropertyPrice(numericValue);
        setFormattedPropertyPrice(CurrencyFormatter(numericValue));
      } else if (name === "downPayment") {
        setDownPayment(numericValue);
        setFormattedDownPayment(CurrencyFormatter(numericValue, false));
      } else if (name === "loanTerm") {
        setLoanTerm(numericValue);
      } else if (name === "loanInterest") {
        setLoanInterest(numericValue);
      }
    };

  const handleInputChange =
    (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue =
        event.target.value === "" ? 0 : Number(event.target.value);
      if (name === "propertyPrice") {
        setPropertyPrice(newValue);
      } else if (name === "downPayment") {
        setDownPayment(newValue);
      } else if (name === "loanTerm") {
        setLoanTerm(newValue);
      } else if (name === "loanInterest") {
        setLoanInterest(newValue);
      }
    };

  const handleDownPaymentInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value.replace(/[^\d,]/g, "").replace(",", ".");
    const numericValue = value === "" ? 0 : parseFloat(value);
    setDownPayment(numericValue);
    setFormattedDownPayment(event.target.value);
  };

  const handleBlurDownPayment = () => {
    setFormattedDownPayment(CurrencyFormatter(downPayment));
  };

  const handleBlur = (name: string) => {
    if (name === "propertyPrice") {
      setFormattedPropertyPrice(CurrencyFormatter(propertyPrice));
    } else if (name === "downPayment") {
      setFormattedDownPayment(CurrencyFormatter(downPayment));
    } else if (name === "loanTerm" && (loanTerm < 1 || loanTerm > 40)) {
      setLoanTerm((prev) => Math.max(1, Math.min(prev, 40)));
    }
  };

  return (
    <Paper sx={{ padding: 3 }}>
      <Typography fontWeight="700" fontSize="1.2rem" marginBottom={2}>
        {t("mortgage_calculator.title")}
      </Typography>
      <Box
        sx={{ paddingX: 1, display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Grid container spacing={0}>
          <Grid item xs={6} sm={4} md={4} lg={3}>
            <Typography variant="subtitle1" gutterBottom>
              {t("mortgage_calculator.property_price")}
            </Typography>
          </Grid>
          <Grid item fontWeight="700" fontSize="1.2rem" xs={6} sm={4}>
            {formattedPropertyPrice}
          </Grid>
        </Grid>

        <Grid container spacing={1} alignItems="center" marginBottom={2}>
          <Grid item xs={6} sm={4} lg={3}>
            <Typography gutterBottom>
              {t("mortgage_calculator.down_payment")}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              value={formattedDownPayment}
              onChange={handleDownPaymentInputChange}
              onBlur={handleBlurDownPayment}
              type="text"
              InputProps={{
                endAdornment: <InputAdornment position="end">€</InputAdornment>,
                sx: { input: { textAlign: "right" } },
              }}
              inputProps={{
                step: 1000,
                min: propertyPrice * 0.1,
                max: propertyPrice * 0.7,
                "aria-labelledby": "down-payment-slider",
              }}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4} lg={5}>
            <Typography
              component="strong"
              fontWeight="bold"
              sx={{ paddingLeft: { sm: 3 } }}
            >
              {t("mortgage_calculator.down_payment_percentage", {
                percentage: ((downPayment / propertyPrice) * 100).toFixed(0),
              })}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8} lg={7}>
            <Slider
              value={downPayment}
              onChange={handleSliderChange("downPayment")}
              min={propertyPrice * 0.1}
              max={propertyPrice * 0.7}
              step={1000}
              aria-labelledby="down-payment-slider"
            />
          </Grid>
        </Grid>

        <Grid container spacing={1} alignItems="center" marginBottom={2}>
          <Grid item xs={6} sm={4} md={4} lg={3}>
            <Typography gutterBottom>
              {t("mortgage_calculator.loan_term")}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4} md={4} lg={4}>
            <TextField
              value={loanTerm}
              onChange={handleInputChange("loanTerm")}
              onBlur={() => handleBlur("loanTerm")}
              type="number"
              InputProps={{
                sx: { input: { textAlign: "right" } },
              }}
              inputProps={{
                min: 5,
                max: 40,
              }}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={8} lg={7}>
            <Slider
              value={loanTerm}
              onChange={handleSliderChange("loanTerm")}
              min={5}
              max={40}
            />
          </Grid>
        </Grid>

        <Grid container spacing={1} alignItems="center" marginBottom={2}>
          <Grid item xs={6} sm={4} lg={3}>
            <Typography gutterBottom>
              {t("mortgage_calculator.interest_rate")}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              value={loanInterest}
              onChange={handleInputChange("loanInterest")}
              onBlur={() => handleBlur("loanInterest")}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                sx: { input: { textAlign: "right" } },
              }}
              type="number"
              inputProps={{
                step: 0.1,
                min: 0.5,
                max: 5,
                "aria-labelledby": "slider-interest-rate",
              }}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={8} lg={7}>
            <Slider
              value={loanInterest}
              onChange={handleSliderChange("loanInterest")}
              min={0.5}
              max={5}
              step={0.1}
              aria-labelledby="slider-interest-rate"
            />
          </Grid>
        </Grid>
        <Divider />
        <Grid sx={{ padding: 3, paddingBottom: 1 }} container spacing={0}>
          <Grid item xs={6} sm={6} md={4} lg={5}>
            <Typography fontWeight="600" fontSize="1.1rem" gutterBottom>
              {t("mortgage_calculator.taxes_and_expenses")}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={6} md={8} lg={7}>
            <Typography
              fontWeight="600"
              fontSize="1.1rem"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              {CurrencyFormatter(totalExpenses)}
              <span style={{ marginLeft: 10, cursor: "pointer" }}>
                <InfoButtonModal
                  notaryExpenses={notaryExpenses}
                  registryExpenses={registryExpenses}
                  taxesExpenses={taxesExpenses}
                  totalExpenses={totalExpenses}
                />
              </span>
            </Typography>
          </Grid>
          <Grid item xs={6} sm={6} md={4} lg={5}>
            <Typography fontWeight="600" fontSize="1.1rem" gutterBottom>
              {t("mortgage_calculator.loan_amount")}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={6} md={8} lg={7}>
            <Typography fontWeight="600" fontSize="1.1rem" gutterBottom>
              {CurrencyFormatter(loanValue)}
            </Typography>
          </Grid>
        </Grid>
        <Divider />
        <Grid sx={{ padding: 3, paddingBottom: 0 }} container spacing={0}>
          <Grid item xs={6} sm={6} md={4} lg={5}>
            <Typography fontWeight="800" fontSize="1.1rem" gutterBottom>
              {t("mortgage_calculator.monthly_quota")}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={6} md={8} lg={7}>
            <Typography fontWeight="800" fontSize="1.5rem" gutterBottom>
              {CurrencyFormatter(mortgagePayment)}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default MortgageCalculator;
